import { useCallback, useEffect, useState } from 'react'
import { Sparkles } from 'lucide-react'
import { queryAI } from '@/api/ai'
import { fetchAgentAiAnalyses, fetchAgentAiAnalysis } from '@/api/agents'
import { AiAnswer } from '@/components/ai/AiAnswer'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { formatDateTime } from '@/lib/formatAgent'

const ANALYSE_QUESTION = [
  'Analyse the overall health of this device using the full stored dossier.',
  'Give a clear verdict, what needs action, what to watch, what looks healthy,',
  'and practical next checks for an MSP technician.',
].join(' ')

export function DeviceHealthAnalysis({ agentId }) {
  const [history, setHistory] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [answer, setAnswer] = useState('')
  const [meta, setMeta] = useState(null)
  const [analysing, setAnalysing] = useState(false)
  const [loadingHistory, setLoadingHistory] = useState(true)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [error, setError] = useState('')
  const [panelOpen, setPanelOpen] = useState(false)

  const showAnalysis = useCallback(async (analysisId) => {
    if (!agentId || !analysisId) return
    setLoadingDetail(true)
    setError('')
    setPanelOpen(true)
    try {
      const detail = await fetchAgentAiAnalysis(agentId, analysisId)
      setSelectedId(detail.id)
      setAnswer(detail.answer || '')
      setMeta({
        created_at: detail.created_at,
        provider: detail.provider,
        model: detail.model,
        created_by_email: detail.created_by_email,
      })
    } catch {
      setError('Could not load that analysis.')
    } finally {
      setLoadingDetail(false)
    }
  }, [agentId])

  useEffect(() => {
    let cancelled = false
    setLoadingHistory(true)
    setError('')
    setAnswer('')
    setMeta(null)
    setSelectedId(null)
    setPanelOpen(false)
    setHistory([])

    fetchAgentAiAnalyses(agentId)
      .then(async (items) => {
        if (cancelled) return
        setHistory(items)
        if (items.length > 0) {
          setPanelOpen(true)
          setLoadingDetail(true)
          try {
            const detail = await fetchAgentAiAnalysis(agentId, items[0].id)
            if (cancelled) return
            setSelectedId(detail.id)
            setAnswer(detail.answer || '')
            setMeta({
              created_at: detail.created_at,
              provider: detail.provider,
              model: detail.model,
              created_by_email: detail.created_by_email,
            })
          } catch {
            if (!cancelled) setError('Could not load the latest analysis.')
          } finally {
            if (!cancelled) setLoadingDetail(false)
          }
        }
      })
      .catch(() => {
        if (!cancelled) setHistory([])
      })
      .finally(() => {
        if (!cancelled) setLoadingHistory(false)
      })

    return () => {
      cancelled = true
    }
  }, [agentId])

  async function handleAnalyse() {
    if (!agentId || analysing) return
    setPanelOpen(true)
    setAnalysing(true)
    setError('')
    try {
      const result = await queryAI({
        question: ANALYSE_QUESTION,
        scope: 'device_health',
        agent_id: Number(agentId),
      })
      setAnswer(result.answer || '')
      setMeta({
        created_at: result.created_at,
        provider: result.provider,
        model: result.model,
        created_by_email: null,
      })
      if (result.analysis_id) {
        setSelectedId(result.analysis_id)
      }
      const items = await fetchAgentAiAnalyses(agentId)
      setHistory(items)
      if (result.analysis_id) {
        setSelectedId(result.analysis_id)
      } else if (items[0]) {
        setSelectedId(items[0].id)
      }
    } catch (err) {
      if (err.code === 'ECONNABORTED') {
        setError('The analysis timed out. Please try again.')
      } else {
        setError(
          err.response?.data?.detail
            || 'Could not analyse this device. Check AI settings and try again.',
        )
      }
    } finally {
      setAnalysing(false)
    }
  }

  function handleSelectHistory(event) {
    const nextId = Number(event.target.value)
    if (!nextId) return
    showAnalysis(nextId)
  }

  const hasHistory = history.length > 0

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={handleAnalyse}
          disabled={!agentId || analysing}
        >
          <Sparkles className="h-4 w-4" />
          {analysing ? 'Analysing…' : hasHistory ? 'Analyse again' : 'Analyse device'}
        </Button>
      </div>

      {(panelOpen || hasHistory || loadingHistory) && (
        <Card>
          <CardContent className="space-y-3 pt-5">
            <div>
              <p className="font-medium">AI device health analysis</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Each run is saved with a date so you can browse the history. Uses the
                stored device dossier in Bekat Dash — not a live query of the machine.
              </p>
            </div>

            {hasHistory && (
              <div className="max-w-md space-y-2">
                <Label htmlFor="device-ai-history">History</Label>
                <select
                  id="device-ai-history"
                  className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
                  value={selectedId || ''}
                  onChange={handleSelectHistory}
                  disabled={analysing || loadingDetail}
                >
                  {history.map((item) => (
                    <option key={item.id} value={item.id}>
                      {formatDateTime(item.created_at)}
                      {item.model ? ` · ${item.model}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {meta?.created_at && (
              <p className="text-xs text-muted-foreground">
                Saved {formatDateTime(meta.created_at)}
                {meta.model ? ` · ${meta.provider || 'AI'} / ${meta.model}` : ''}
                {meta.created_by_email ? ` · ${meta.created_by_email}` : ''}
              </p>
            )}

            {error && <p className="text-sm text-destructive">{error}</p>}
            {(analysing || loadingDetail) && !answer && !error && (
              <p className="text-sm text-muted-foreground">
                {analysing ? 'Building dossier and analysing…' : 'Loading saved analysis…'}
              </p>
            )}
            {answer && <AiAnswer answer={answer} />}
            {!loadingHistory && !hasHistory && !answer && !analysing && !error && (
              <p className="text-sm text-muted-foreground">
                No saved analyses yet. Select Analyse device to create the first one.
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
